import * as React from 'react';

import Select from 'antd/lib/select';

import { v4 } from 'uuid';

import { useTranslation } from 'react-i18next';

import { isPresent, Nullable } from '@common/typescript/objects/Nullable';
import { isPresent as isNotRemoved } from '@common/typescript/objects/WithRemoved';
import { List } from '@common/typescript/objects/List';

import { Price, PriceKind, PriceType } from '@app/objects/Price';
import { ServiceType } from '@app/objects/Pet';
import { useRequest } from '@app/hooks/useRequest';
import { useCrematoryFeatures } from '@app/hooks/Crematory/useCrematoryFeatures';
import { clearPreset, PetPrice } from '@app/objects/PetPrice';

interface PriceListMessage {
	crematoryId?: number;
	serviceType?: ServiceType;
	priceType?: PriceType;

	filters: {
		priceKind: [PriceKind.Preset];
	}

	withSpecialService?: boolean;
	withInventoryItem?: boolean;
	withStoreEntry?: boolean;
}

interface PricePresetSelectProps {
	value: Nullable<number> | undefined;
	onChange: (value: Nullable<number>, price: Nullable<Price>) => void;
	presets: Array<Price>;
	multiple?: boolean;
	onDeselect?: (fieldName: string, type: string, id?: number) => void;
}

interface PresetFilterSelectProps {
	crematoryId: Nullable<number>;
	value: Nullable<number> | undefined;

	disabled?: boolean;
	withFieldName?: boolean;

	className?: string;
	selectClassName?: (value: string | Nullable<number> | undefined, length: number) => string;

	onChange: (value: Nullable<number>) => void;
	multiple?: boolean;
	onDeselect?: (fieldName: string, type: string, id?: number) => void;
}

function toPresetPetPrice(preset: Price, petId: number): PetPrice {
	const parent: PetPrice = {
		id: -1,
		clientId: v4(),

		petId,
		pet: null,

		priceId: preset.id,
		price: preset,

		count: preset.batchCount,
		completedCount: 0,
		done: false,

		value: preset.value,
		extra: preset.extra,
		discount: 0,
		tax: 0,

		name: preset.name,

		editor: null,
		editorId: null,

		pickupService: null,
		pickupServiceId: null,

		batchCount: 0,
		batchPrice: 0,

		entry: null,
		node: null,
		nodeId: null,

		note: '',

		engravingId: null,
		engraving: null,

		parent: null,
		parentId: null,
	};
	parent.children = (preset.children ?? []).map((price: Price): PetPrice => ({
		id: -1,
		clientId: v4(),

		pet: null,
		petId,

		price,
		priceId: price.id,

		count: price.batchCount,
		completedCount: 0,
		done: false,

		value: price.value,
		extra: 0,
		discount: 0,
		tax: 0,

		name: price.name,

		editor: null,
		editorId: null,

		pickupService: null,
		pickupServiceId: null,

		batchCount: 0,
		batchPrice: 0,

		entry: null,
		node: null,
		nodeId: price.inventoryItem?.storeNode?.id ?? null,
		note: '',

		engravingId: null,
		engraving: null,

		parent,
		parentId: parent.id,
	}));

	return parent;
}

function usePresets(crematoryId?: Nullable<number>, serviceType?: ServiceType, priceType?: PriceType): Array<Price> {
	const request = useRequest<List<Price>, PriceListMessage>('priceList', undefined, { requestOnMount: false });

	React.useEffect(() => {
		if (!isPresent(crematoryId)) return;

		request.reload({
			crematoryId,
			serviceType,
			priceType,
			filters: {
				priceKind: [PriceKind.Preset],
			},
			withSpecialService: true,
			withInventoryItem: true,
		});
	}, [crematoryId, serviceType, priceType]);

	if (crematoryId === null) return [];
	if (!request.item) return [];

	return request.item.list;
}

export const PricePresetSelect: React.FC<PricePresetSelectProps> = (props: PricePresetSelectProps) => {
	return (
		<div>
			<label htmlFor="preset-select">Preset</label>
			<Select
				id="preset-select"
				mode={props.multiple ? 'multiple' : undefined}
				value={props.value}
				onDeselect={(value: Nullable<number>) => props.onDeselect && props.onDeselect('preset', 'id', value || 0)}
				onChange={(selectedValue: Nullable<number>) => {
					const price = selectedValue ? props.presets.find((p) => p.id === selectedValue) ?? null : null;
					props.onChange(selectedValue, price);
				}}
				placeholder="Pick a preset"
				allowClear
			>
				{
					props.presets.map((item: Price) => (
						<Select.Option key={item.id} value={item.id}>
							{item.name}
						</Select.Option>
					))
				}
			</Select>
		</div>
	);
};

export function clearRelatedPresetServices<T extends Values>(values: T): T {
	return {
		...values,

		services: clearPreset(values.services ?? []),
		urns: clearPreset(values.urns ?? []),
		products: clearPreset(values.products ?? []),
	};
}

/**
 * clearServices - when preset is selected, all existing services, urns and products should be removed
 * @param items
 */
function clearServices(items: Array<PetPrice>): Array<PetPrice> {
	const exclude: Array<PriceKind> = [PriceKind.SpecialServicePrice, PriceKind.ProductPrice, PriceKind.UrnPrice, PriceKind.Preset];

	return items.filter((item: PetPrice) => !item.price || !exclude.includes(item.price.priceKind));
}

interface Values {
	id: number;

	services?: Array<PetPrice>;
	urns?: Array<PetPrice>;
	products?: Array<PetPrice>;
}

type Mutator<T extends Values> = (value: T) => T;

interface OwnProps<T extends Values> {
	serviceType: ServiceType;
	priceType: PriceType;
	value: T;
	onChange: (mutator: Mutator<T>) => void;
}

export const PetPricePresetSelect = <T extends Values>(props: OwnProps<T>) => {
	const features = useCrematoryFeatures();
	const presets = usePresets(features.item?.crematoryId, props.serviceType, props.priceType);

	if (!features.item?.enablePricePreset) return null;
	if (presets.length === 0) return null;

	const services = props.value.services ?? [];
	const value = services
		.filter(isNotRemoved)
		.find((item: PetPrice) => item.price?.priceKind === PriceKind.Preset)
		?.priceId;

	return (
		<PricePresetSelect
			value={value}
			onChange={(value: Nullable<number>, price: Nullable<Price>) => {
				if (!price) {
					props.onChange(clearRelatedPresetServices);

					return;
				}

				const mutator = (values: T) => {
					const presetItem = toPresetPetPrice(price, values.id);
					const children = presetItem.children ?? [];
					const services = clearServices(values.services ?? []);
					const urns: Array<PetPrice> = children.filter((c) => c.price?.priceKind === PriceKind.UrnPrice);
					const products: Array<PetPrice> = children.filter((c) => c.price?.priceKind === PriceKind.ProductPrice);

					services.push(presetItem);
					services.push(...children.filter((c) => c.price?.priceKind === PriceKind.SpecialServicePrice));

					return {
						...values,

						services,
						urns,
						products,
					};
				};

				props.onChange(mutator);
			}}
			presets={presets}
		/>
	);
};

export const PresetFilterSelect: React.FC<PresetFilterSelectProps> = (props : PresetFilterSelectProps) => {
	const { t } = useTranslation();
	const translator = React.useCallback((key: string) => t(`filters.${key}`), []);
	const [query, setQuery] = React.useState<string>('');
	const presets = usePresets(props.crematoryId);

	return (
		<div className={props.className}>
			{props.withFieldName && <span className="field-name">{translator('preset')}</span>}
			<span className="ant-select-selection__placeholder">{translator('preset')}</span>
			<Select
				className={props.selectClassName ? props.selectClassName(props.value, query.length) : ''}
				showSearch
				showArrow
				allowClear
				onDeselect={(value: Nullable<number>) => props.onDeselect && props.onDeselect('preset', 'id', value || 0)}
				mode={props.multiple ? 'multiple' : undefined}
				showAction={['focus', 'click']}
				optionFilterProp="label"
				placeholder={translator('preset')}
				value={props.value}
				onChange={(value: Nullable<number>) => props.onChange(typeof value === 'undefined' ? null : value)}
				onSearch={(query: string) => setQuery(query)}
				onSelect={() => setQuery('')}
				onBlur={() => setQuery('')}
				disabled={props.disabled}
			>
				{presets.map((item) => (
					<Select.Option
						key={item.id}
						value={item.id}
						label={item.name}
					>
						{item.name}
					</Select.Option>
				))}
			</Select>
		</div>
	);
};
